import { Component, inject, Input, Sanitizer, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, IonicModule } from '@ionic/angular';
import { DateTime } from 'luxon';
import { map, Observable } from 'rxjs';
import { AppData } from 'src/app/data';
import { BaseJob, Job, Dropoff, JobType, JobTypeLabels, ScheduleWindow } from 'src/app/types';
import { ImageService } from 'src/app/services';
import { calculateJobFare, formatCurrency } from 'src/app/helpers/ui-helpers';
import { environment } from 'src/environments/environment';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { RouteEventCardComponent } from '../../components/route-event-card/route-event-card.component';
import { NgClass, AsyncPipe } from '@angular/common';
import { AddressComponent } from '../../components/address/address.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  templateUrl: './job-details.dialog.html',
  styleUrls: ['./job-details.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IonicModule,
    RouteEventCardComponent,
    NgClass,
    AddressComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class JobDetailsDialog {
  private readonly data = inject(AppData);
  private readonly imageSvc = inject(ImageService);
  private readonly modalCtrl = inject(ModalController);
  private readonly domSanitizer = inject(DomSanitizer);

  private readonly gtm = (<any>window).dataLayer || [];
  public readonly hasNetwork$ = this.data.connectionStatus$.pipe(map(value => !!value.connected));

  public job: Job = null;
  public riderImage$: Observable<SafeUrl | null>;
  public dropoff: Dropoff | null = null;
  public fare: string;
  public readonly features = environment.features;
  public readonly translationKeys = environment.translationKeys;
  public priorityTime: string;
  public scheduleWindow: string | null = null;
  public readonly jobType = JobType;
  public riderIdLabel = JobTypeLabels[this.job?.value.jobType];

  @Input() set routeEvent(routeEvent: Job) {
    this.job = routeEvent;
    this.riderImage$ = this.imageSvc
      .getRiderImage({
        hasNetwork$: this.hasNetwork$,
        sessionId: this.data.sessionId,
        riderImageId: this.job.value.riderImageId,
      })
      .pipe(map(url => (url ? this.domSanitizer.bypassSecurityTrustResourceUrl(url) : null)));
    this.dropoff = environment.features.showDropoffWithPickup ? this.job.dropoff : null;
    this.fare = this.getFormattedFare(this.job.value);
    const { riderImageId, rideComments, riderComments } = routeEvent.value;
    this.priorityTime = DateTime.fromISO(
      (routeEvent.value.priorityTime ?? routeEvent.value.scheduledTime).toString(),
    ).toFormat('HH:mm');
    this.riderIdLabel = JobTypeLabels[this.job?.value.jobType];
    this.scheduleWindow = this.getScheduleWindow(this.job.value);
  }

  constructor() {
    this.gtm.push({ event: 'custom_event', custom_event_name: 'view_ job_details' });
  }

  private getFormattedFare(job: BaseJob): string {
    const fare = calculateJobFare(job);
    return formatCurrency(fare);
  }

  /** Pickup jobs use `pickupWindow`, dropoff jobs use `dropoffWindow`; null when there is nothing to show */
  private getScheduleWindow(job: BaseJob): string | null {
    switch (job.jobType) {
      case JobType.Pickup: return this.formatWindow(job.pickupWindow);
      case JobType.DropOff: return this.formatWindow(job.dropoffWindow);
      default: return null;
    }
  }

  private formatWindow(window?: ScheduleWindow | null): string | null {
    if (!window?.start || !window?.end) { return null; }
    const start = DateTime.fromISO(window.start.toString());
    const end = DateTime.fromISO(window.end.toString());
    if (!start.isValid || !end.isValid) { return null; }
    return `${start.toFormat('HH:mm')} - ${end.toFormat('HH:mm')}`;
  }

  public dismissDialog(): void {
    this.modalCtrl.dismiss();
  }
}
